import { useCallback, useEffect, useRef, useState } from 'react';

import { HighlightedMenuItem, MenuItem, MenuItemWithTag } from './filter-combo-box.types';
import { addStyles, CheckMark, mergeStyles, TableTag } from '../..';

const itemStyles = {
  base: 'box-border p-2 border border-1 border-solid cursor-pointer focus:border-gray-900 hover:border-gray-100',
  unselected: 'bg-white focus:bg-white hover:bg-gray-50 border-white',
  selected: 'focus:bg-jungle-50 hover:bg-jungle-100 bg-jungle-50 border-jungle-50',
};

const iconStyles = {
  base: 'h-4 w-12 inline',
  unselected: 'fill-transparent',
  selected: 'fill-gray-900',
};

export const highlight = (str: string, highlighted?: string) => {
  if (!highlighted || highlighted == '' || !str.toLowerCase().includes(highlighted.toLowerCase())) return str;

  const parts = str.split(new RegExp(`(${highlighted})`, 'gi')).map((value, index) => ({
    value,
    key: value.slice(0, index) + str + index,
  }));

  return (
    <>
      {parts.map((part) => (
        <span
          key={part.key}
          className={part.value.toLowerCase() === highlighted.toLowerCase() ? 'font-bold text-primary' : ''}
        >
          {part.value}
        </span>
      ))}
    </>
  );
};

export interface MenuItemProps<T extends MenuItem> {
  item: T;
  className?: string;
  onSelect: (item: T, isSelected: boolean) => void;
  renderItem?: (item: T, isSelected: boolean) => React.ReactNode;
  defaultSelection?: boolean;
}

export const matchesSearch = <T extends MenuItemWithTag>(item: T, searchTerm: string) => {
  const search = searchTerm.toLowerCase();
  return (
    item.label.toLowerCase().includes(search) ||
    item.value.toLowerCase().includes(search) ||
    item.tagText?.toLowerCase().includes(search)
  );
};

export const defaultRenderListItem = <T extends HighlightedMenuItem>(item: T, isSelected: boolean) => (
  <>
    {<CheckMark className={addStyles([iconStyles.base, isSelected ? iconStyles.selected : iconStyles.unselected])} />}
    {highlight(item.label, item.textToHighlight)}
  </>
);

export const renderListItemWithTag = <T extends MenuItemWithTag>(item: T, isSelected: boolean) => (
  <div className="flex items-start">
    <div className="w-10">
      {<CheckMark className={'h-4 inline mt-1 ' + (isSelected ? 'fill-gray-900' : 'fill-transparent')} />}
    </div>
    <div className="grow pl-3">
      <div>{highlight(item.label, item.textToHighlight)}</div>
      <div className="pt-1 text-xs text-gray-700">{item.subtext}</div>
    </div>
    <div className="whitespace-nowrap">
      <TableTag
        type="default"
        className="rounded"
        text={item.tagText ?? ''}
        leftIcon={item.tagLeftIcon}
        leftIconClassName="h-4 w-4 mr-1 inline"
      />
    </div>
  </div>
);

export const FilterComboBoxMenuItem = <T extends MenuItem>(props: MenuItemProps<T>) => {
  const { item, className, onSelect, defaultSelection, renderItem = defaultRenderListItem } = props;
  const [isSelected, setIsSelected] = useState<boolean>(defaultSelection ?? false);
  const ref = useRef<HTMLDivElement>(null);

  // ensure that future updates to the selection state are applied to the DOM
  useEffect(() => setIsSelected(defaultSelection === true), [defaultSelection]);

  const onClick = useCallback(
    (e: { stopPropagation: () => void }) => {
      onSelect(item, !isSelected);
      setIsSelected(!isSelected);
      e.stopPropagation();
    },
    [isSelected, item, onSelect]
  );

  return (
    <div
      role="menuitem"
      tabIndex={0}
      ref={ref}
      onClick={onClick}
      title={item.label}
      onMouseUp={() => {
        (ref.current as HTMLDivElement).blur();
      }}
      onMouseLeave={() => {
        (ref.current as HTMLDivElement).blur();
      }}
      className={mergeStyles([itemStyles.base, itemStyles[isSelected ? 'selected' : 'unselected'], className])}
    >
      <span className="select-none">{renderItem(item, isSelected)}</span>
    </div>
  );
};
